import React, { useCallback, useRef, useEffect } from 'react';
import ReactFlow, {
  ReactFlowProvider,
  addEdge,
  useNodesState,
  useEdgesState,
  Controls,
  MiniMap,
  Background,
  BackgroundVariant,
  Connection,
  Edge,
  Node,
  NodeTypes,
  EdgeTypes,
  ReactFlowInstance,
  Panel,
  ConnectionLineType
} from 'reactflow';
import 'reactflow/dist/style.css';

import { useTheme } from '@mui/material';
import { WorkflowNode, WorkflowEdge, WorkflowDefinition, NODE_COLORS, NodeType } from '../../../types/workflow.types';

// Import custom node components
import StartNode from './nodes/StartNode';
import TeammateNode from './nodes/TeammateNode';
import HumanTaskNode from './nodes/HumanTaskNode';
import ApiCallNode from './nodes/ApiCallNode';
import ConditionNode from './nodes/ConditionNode';
import LoopNode from './nodes/LoopNode';
import EndNode from './nodes/EndNode';

// Import custom edge components
import DefaultEdge from './edges/DefaultEdge';
import ConditionalEdge from './edges/ConditionalEdge';

// Import toolbar
import WorkflowToolbar from './WorkflowToolbar';

const nodeTypes: NodeTypes = {
  start: StartNode,
  teammate: TeammateNode,
  human: HumanTaskNode,
  api: ApiCallNode,
  condition: ConditionNode,
  loop: LoopNode,
  end: EndNode,
};

const edgeTypes: EdgeTypes = {
  default: DefaultEdge,
  conditional: ConditionalEdge,
};

interface WorkflowCanvasProps {
  definition: WorkflowDefinition;
  onChange: (definition: WorkflowDefinition) => void;
  onValidate?: (definition: WorkflowDefinition) => void;
  readOnly?: boolean;
  selectedNodeId?: string;
  onNodeSelect?: (nodeId: string | null, currentNodes?: WorkflowNode[]) => void;
  onEdgeCreate?: (definition: WorkflowDefinition) => void;
}

const WorkflowCanvasInner: React.FC<WorkflowCanvasProps> = ({
  definition,
  onChange,
  onValidate,
  readOnly = false,
  selectedNodeId,
  onNodeSelect,
  onEdgeCreate,
}) => {
  const theme = useTheme();
  const reactFlowWrapper = useRef<HTMLDivElement>(null);

  // Convert WorkflowNode to React Flow Node
  const workflowNodesToReactFlow = (workflowNodes: WorkflowNode[]): Node[] => {
    return workflowNodes.map(node => ({
      ...node,
      type: node.type as string,
    }));
  };

  // Convert WorkflowEdge to React Flow Edge
  const workflowEdgesToReactFlow = (workflowEdges: WorkflowEdge[]): Edge[] => {
    return workflowEdges.map(edge => ({
      ...edge,
      type: edge.type as string,
    }));
  };

  // Convert React Flow Node to WorkflowNode
  const reactFlowNodesToWorkflow = useCallback((reactFlowNodes: Node[]): WorkflowNode[] => {
    return reactFlowNodes.map(node => ({
      ...node,
      type: node.type as NodeType,
    })) as WorkflowNode[];
  }, []);

  // Convert React Flow Edge to WorkflowEdge
  const reactFlowEdgesToWorkflow = useCallback((reactFlowEdges: Edge[]): WorkflowEdge[] => {
    return reactFlowEdges.map(edge => ({
      ...edge,
      type: (edge.type || 'default') as 'default' | 'conditional',
    })) as WorkflowEdge[];
  }, []);

  const [nodes, setNodes, onNodesChange] = useNodesState(workflowNodesToReactFlow(definition.nodes));
  const [edges, setEdges, onEdgesChange] = useEdgesState(workflowEdgesToReactFlow(definition.edges));
  const [reactFlowInstance, setReactFlowInstance] = React.useState<ReactFlowInstance | null>(null);

  // Sync with external definition changes
  useEffect(() => {
    setNodes(workflowNodesToReactFlow(definition.nodes));
    setEdges(workflowEdgesToReactFlow(definition.edges));
  }, [definition, setNodes, setEdges]);

  // Use useRef to persist timeout and onChange function across renders
  const timeoutIdRef = useRef<NodeJS.Timeout | null>(null);
  const onChangeRef = useRef(onChange);

  // Keep the latest onChange function in the ref
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

 // Remove onChange from dependencies

  // TEMPORARILY DISABLED: Auto-save causes request flooding
  // We'll implement manual save button instead
  //
  // useEffect(() => {
  //   // Skip initial render or if in read-only mode
  //   if (readOnly) return;
  //
  //   debouncedOnChange(nodes, edges, definition.variables);
  // }, [nodes, edges, definition.variables, debouncedOnChange, readOnly]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current);
        timeoutIdRef.current = null;
      }
    };
  }, []);

  const onConnect = useCallback(
    (params: Connection) => {
      if (readOnly || !params.source || !params.target) return;

      const newEdge: Edge = {
        ...params,
        source: params.source,
        target: params.target,
        type: 'default',
        id: `e${params.source}-${params.target}`,
      };

      setEdges((eds) => {
        const updatedEdges = addEdge(newEdge, eds);

        // Immediately save the edge if onEdgeCreate callback is provided
        if (onEdgeCreate) {
          const updatedDefinition: WorkflowDefinition = {
            nodes: reactFlowNodesToWorkflow(nodes),
            edges: reactFlowEdgesToWorkflow(updatedEdges),
            variables: definition.variables,
          };
          onEdgeCreate(updatedDefinition);
        }

        return updatedEdges;
      });
    },
    [readOnly, setEdges, onEdgeCreate, nodes, definition.variables, reactFlowNodesToWorkflow, reactFlowEdgesToWorkflow]
  );

  const onNodeClick = useCallback(
    (event: React.MouseEvent, node: Node) => {
      console.log('WorkflowCanvas: Node clicked', node.id, node.type);
      if (onNodeSelect) {
        console.log('WorkflowCanvas: Calling onNodeSelect with current nodes');
        const currentWorkflowNodes = reactFlowNodesToWorkflow(nodes);
        onNodeSelect(node.id, currentWorkflowNodes);
      } else {
        console.log('WorkflowCanvas: onNodeSelect not provided');
      }
    },
    [onNodeSelect, nodes, reactFlowNodesToWorkflow]
  );

  const onPaneClick = useCallback(() => {
    if (onNodeSelect) {
      onNodeSelect(null);
    }
  }, [onNodeSelect]);

  const onDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const onDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();

      if (readOnly || !reactFlowInstance) return;

      const reactFlowBounds = reactFlowWrapper.current?.getBoundingClientRect();
      if (!reactFlowBounds) return;

      const nodeType = event.dataTransfer.getData('application/reactflow');
      if (!nodeType) return;

      const position = reactFlowInstance.project({
        x: event.clientX - reactFlowBounds.left,
        y: event.clientY - reactFlowBounds.top,
      });

      const newNodeId = `${nodeType}_${Date.now()}`;
      const newNode: WorkflowNode = {
        id: newNodeId,
        type: nodeType as any,
        position,
        data: {
          title: getDefaultNodeTitle(nodeType),
          config: {},
          isConnectable: true,
        },
        style: {
          backgroundColor: NODE_COLORS[nodeType as keyof typeof NODE_COLORS],
          color: 'white',
          border: selectedNodeId === newNodeId ? '2px solid #1976d2' : '1px solid #ddd',
        },
      };

      setNodes((nds) => nds.concat(newNode));
    },
    [reactFlowInstance, readOnly, selectedNodeId, setNodes]
  );

  const getDefaultNodeTitle = (nodeType: string): string => {
    const titles: Record<string, string> = {
      start: 'Start',
      teammate: 'AI Teammate',
      human: 'Human Task',
      api: 'API Call',
      condition: 'Condition',
      loop: 'Loop',
      end: 'End',
    };
    return titles[nodeType] || 'Node';
  };

  const onValidateWorkflow = useCallback(() => {
    if (onValidate) {
      onValidate({
        nodes: reactFlowNodesToWorkflow(nodes),
        edges: reactFlowEdgesToWorkflow(edges),
        variables: definition.variables
      });
    }
  }, [nodes, edges, definition.variables, onValidate, reactFlowNodesToWorkflow, reactFlowEdgesToWorkflow]);

  // Update node styles based on selection
  useEffect(() => {
    setNodes((nds) =>
      nds.map((node) => ({
        ...node,
        style: {
          ...node.style,
          border: selectedNodeId === node.id ? '3px solid #1976d2' : '1px solid #ddd',
        },
      }))
    );
  }, [selectedNodeId, setNodes]);

  return (
    <div
      ref={reactFlowWrapper}
      style={{
        width: '100%',
        height: '100%',
        position: 'relative',
        border: '1px solid rgba(0,0,0,0.12)',
        borderRadius: 4,
        overflow: 'hidden',
      }}
    >
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={readOnly ? undefined : onNodesChange}
        onEdgesChange={readOnly ? undefined : onEdgesChange}
        onConnect={readOnly ? undefined : onConnect}
        onInit={setReactFlowInstance}
        onNodeClick={onNodeClick}
        onPaneClick={onPaneClick}
        onDrop={readOnly ? undefined : onDrop}
        onDragOver={readOnly ? undefined : onDragOver}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        connectionLineType={ConnectionLineType.SmoothStep}
        fitView
        fitViewOptions={{
          padding: 0.2,
          includeHiddenNodes: false,
        }}
        defaultViewport={{ x: 0, y: 0, zoom: 1 }}
        minZoom={0.1}
        maxZoom={2}
        nodesDraggable={!readOnly}
        nodesConnectable={!readOnly}
        elementsSelectable={!readOnly}
        selectNodesOnDrag={false}
        style={{
          backgroundColor: theme.palette.background.default,
        }}
      >
        <Panel position="top-left">
          <WorkflowToolbar
            readOnly={readOnly}
            onValidate={onValidateWorkflow}
          />
        </Panel>

        <Controls
          position="bottom-left"
          style={{
            backgroundColor: theme.palette.background.paper,
            border: `1px solid ${theme.palette.divider}`,
          }}
        />

        <MiniMap
          position="bottom-right"
          style={{
            backgroundColor: theme.palette.background.paper,
            border: `1px solid ${theme.palette.divider}`,
          }}
          nodeColor={(node) => {
            return NODE_COLORS[node.type as keyof typeof NODE_COLORS] || theme.palette.primary.main;
          }}
        />

        <Background
          variant={BackgroundVariant.Dots}
          gap={20}
          size={1}
          style={{
            backgroundColor: theme.palette.background.default,
          }}
        />
      </ReactFlow>
    </div>
  );
};

// Wrapper component that provides ReactFlow context
const WorkflowCanvas: React.FC<WorkflowCanvasProps> = (props) => {
  return (
    <ReactFlowProvider>
      <WorkflowCanvasInner {...props} />
    </ReactFlowProvider>
  );
};

export default WorkflowCanvas;
